/**
 * Players tab — table of Player rows with team / position / cost filters.
 * Click a row to open the per-player drawer (PlayerDetailPage).
 *
 * Split out from the legacy combined Teams+Players tab so each Game Data
 * dataset has its own first-class navigation entry.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import {
  type AdminPlayerRow,
  type LeagueName,
  type Position,
  useAdminPlayers,
  useAdminTeams,
  useUpdatePlayerCost,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  FilterSelect,
  Pagination,
} from './shared';
import { PlayerDetailPage } from './PlayerDetailPage';

const PAGE_SIZE = 100;

export function PlayersTab({ league }: { league: LeagueName }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const playerId = searchParams.get('id');

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [teamId, setTeamId] = useState('any');
  const [position, setPosition] = useState<Position | 'any'>('any');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q.trim());
      setOffset(0);
    }, 250);
    return () => window.clearTimeout(t);
  }, [q]);

  const teamsQ = useAdminTeams(undefined, league);
  const teams = teamsQ.data?.data ?? [];

  const { data, isLoading, error } = useAdminPlayers({
    q: debouncedQ || undefined,
    team_id: teamId === 'any' ? undefined : teamId,
    position: position === 'any' ? undefined : position,
    league,
    limit: PAGE_SIZE,
    offset,
  });
  const rows = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  const setPlayer = (id: string | null) => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'players');
    if (id) sp.set('id', id);
    else sp.delete('id');
    setSearchParams(sp, { replace: true });
  };

  if (playerId) {
    return <PlayerDetailPage playerId={playerId} onBack={() => setPlayer(null)} />;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder="Search player name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <FilterSelect
          label="Team"
          value={teamId}
          onChange={(v) => {
            setTeamId(v);
            setOffset(0);
          }}
          options={[
            { value: 'any', label: 'All teams' },
            ...teams.map((t) => ({ value: t.team_id, label: t.name })),
          ]}
        />
        <FilterSelect
          label="Position"
          value={position}
          onChange={(v) => {
            setPosition(v as Position | 'any');
            setOffset(0);
          }}
          options={[
            { value: 'any', label: 'All' },
            { value: 'GK', label: 'GK' },
            { value: 'DEF', label: 'DEF' },
            { value: 'MID', label: 'MID' },
            { value: 'FWD', label: 'FWD' },
          ]}
        />
      </div>

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<AdminPlayerRow>
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => setPlayer(r.id)}
          columns={[
            {
              key: 'name',
              label: 'Name',
              render: (r) => <span className="font-medium text-foreground">{r.name}</span>,
            },
            { key: 'team_name', label: 'Team', render: (r) => r.team_name },
            { key: 'position', label: 'Position', render: (r) => r.position },
            {
              key: 'cost',
              label: league === 'WC' ? 'Price' : 'Price (2x FPL)',
              align: 'right',
              render: (r) => <PlayerCostCell player={r} />,
            },
            {
              key: 'active',
              label: 'Active',
              render: (r) => (r.is_active ? 'Yes' : 'No'),
            },
            {
              key: 'external_id',
              label: 'External ID',
              render: (r) => <code className="text-xs">{r.external_id}</code>,
            },
            ...(league === 'WC'
              ? [
                  {
                    key: 'country',
                    label: 'Country',
                    render: (r: AdminPlayerRow) => r.country_code ?? DASH,
                  },
                  {
                    key: 'eliminated',
                    label: 'Eliminated',
                    render: (r: AdminPlayerRow) => (r.is_eliminated ? 'Yes' : 'No'),
                  },
                ]
              : []),
          ]}
        />
        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </DataTableShell>
    </div>
  );
}

// ---------------- Inline cost editor -----------------
//
// Click the cost cell → input becomes editable. Save on blur or Enter,
// cancel on Escape. cost=0 displays "—" with a "Not priced" hint so
// admins know to run `wc-sync-prices`.

function PlayerCostCell({ player }: { player: AdminPlayerRow }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(String(player.cost));
  const mutation = useUpdatePlayerCost();

  // Reset the draft if the upstream cost changes (e.g. cache invalidation
  // after a save) and we're not actively editing.
  useEffect(() => {
    if (!editing) setDraft(String(player.cost));
  }, [player.cost, editing]);

  const commit = () => {
    const next = parseInt(draft, 10);
    if (Number.isNaN(next) || next < 0 || next > 100) {
      setDraft(String(player.cost));
      setEditing(false);
      return;
    }
    if (next === player.cost) {
      setEditing(false);
      return;
    }
    mutation.mutate(
      { playerId: player.id, cost: next },
      {
        onSettled: () => setEditing(false),
      },
    );
  };

  const cancel = () => {
    setDraft(String(player.cost));
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        type="number"
        min={0}
        max={100}
        step={1}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') cancel();
        }}
        onClick={(e) => e.stopPropagation()}
        className="h-6 w-16 rounded border border-input bg-background px-1 text-right text-sm"
        disabled={mutation.isPending}
      />
    );
  }

  const isUnpriced = player.cost === 0;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={cn(
        'rounded px-1 hover:bg-accent',
        isUnpriced && 'text-muted-foreground italic',
      )}
      title={isUnpriced ? 'Not priced — run `wc-sync-prices` or set manually' : 'Click to edit'}
    >
      {isUnpriced ? '—' : player.cost}
    </button>
  );
}
